"use client";

import { ReactNode, useEffect, useState } from "react";
import { AuthContext, AuthContextType } from "@/hooks/auth/useAuth";
import { UserData } from "@/types/api/login";
import { getProfile } from "@/api/services/login";
import {
  setRefreshTokenCookie,
  removeRefreshTokenCookie,
  setAccessTokenCookie,
  removeAccessTokenCookie,
  refreshAccessToken,
} from "@/lib/auth-cookies";
import { getAccessTokenFromCookie } from "@/lib/client-cookies";
import { useRouter } from "next/navigation";
import { useQueryClient, useQuery } from "@tanstack/react-query";

interface AuthProviderProps {
  children: ReactNode;
  initialHasRefreshToken?: boolean;
}

export default function AuthProvider({
  children,
  initialHasRefreshToken,
}: AuthProviderProps) {
  const [hasRefreshToken, setHasRefreshToken] = useState<boolean>(
    initialHasRefreshToken ?? false
  );
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const queryClient = useQueryClient();

  const {
    data: userProfile,
    refetch: refetchUserProfile,
    isLoading: isProfileLoading,
  } = useQuery<UserData | null>({
    queryKey: ["userProfile"],
    queryFn: async () => {
      if (!hasRefreshToken) return null;
      try {
        const response = await getProfile();
        return response;
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
        return null;
      }
    },
    enabled: hasRefreshToken,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const logout = async () => {
    setHasRefreshToken(false);
    await removeRefreshTokenCookie();
    await removeAccessTokenCookie();
    // Clear all queries when user logs out
    queryClient.clear();
    router.push("/");
  };

  // synchronous method to check current authentication state
  const getCurrentAuthState = (): boolean => {
    // Use the state value, not the cookie (cookie is httpOnly)
    return hasRefreshToken;
  };

  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);

      // Try to get access token from client-side cookie
      // Even if server said no refresh token, it might exist client-side
      // (e.g., when navigating from external apps where cookies weren't sent on first request)
      const existingAccessToken = getAccessTokenFromCookie();

      if (existingAccessToken || hasRefreshToken) {
        // If we have an access token client-side but server didn't know about refresh token,
        // update our state
        if (existingAccessToken && !hasRefreshToken) {
          setHasRefreshToken(true);
        }

        // Ensure access token exists, refresh if needed
        if (!existingAccessToken) {
          const result = await refreshAccessToken();
          if (result.success && result.access_token) {
            await setAccessTokenCookie(result.access_token);
          }
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAuth = async (
    newAccessToken: string,
    newRefreshToken: string,
    userData: UserData
  ) => {
    // Set user data in query cache immediately
    queryClient.setQueryData(["userProfile"], userData);
    setHasRefreshToken(true);

    // store both tokens in secure cookies
    await setAccessTokenCookie(newAccessToken);
    await setRefreshTokenCookie(newRefreshToken);
  };

  const value: AuthContextType = {
    user: userProfile ?? null,
    isProfileLoading,
    isLoading,
    isAuthenticated: hasRefreshToken,
    setAuth,
    logout,
    fetchUserProfile: async () => {
      await refetchUserProfile();
    },
    getCurrentAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
